import FirebaseAdapter from "@wc/adapters/firebase";

/**
 * Get the user's primary team in an organization
 * @param {string} userId The user id
 * @param {string} orgId The organization id
 * @returns {Promise<string|null>} The primary team id or null
 */
export async function getPrimaryTeam(userId, orgId) {
  const membership = await FirebaseAdapter().membership.getOrgMembership(
    userId,
    orgId,
  );

  return membership?.primaryTeamId || null;
}

/**
 * Set the user's primary team in an organization
 * @param {string} userId The user id
 * @param {string} orgId The organization id
 * @param {string} teamId The team id to set as primary
 * @returns {Promise<void>}
 */
export async function setPrimaryTeam(userId, orgId, teamId) {
  // Verify user is a member of this team
  const isMember = await FirebaseAdapter().membership.checkTeamAccess(
    userId,
    teamId,
  );

  if (!isMember) {
    throw new Error("User is not a member of this team");
  }

  // Verify team belongs to this org
  const team = await FirebaseAdapter().teams.getTeam(teamId);
  if (!team || team.organizationId !== orgId) {
    throw new Error("Team does not belong to this organization");
  }

  await FirebaseAdapter().membership.setPrimaryTeam(userId, orgId, teamId);
}

/**
 * Get the user's teams in an organization with primary status
 * @param {string} userId The user id
 * @param {string} orgId The organization id
 * @returns {Promise<Array<{id: string, name: string, isPrimary: boolean}>>} Teams with primary flag
 */
export async function getUserTeamsWithPrimary(userId, orgId) {
  const [teamIds, membership] = await Promise.all([
    FirebaseAdapter().membership.listUserTeamsInOrg(userId, orgId),
    FirebaseAdapter().membership.getOrgMembership(userId, orgId),
  ]);

  const primaryTeamId = membership?.primaryTeamId || null;

  // Get team details for each
  const teams = await Promise.all(
    teamIds.map(async (teamId) => {
      const team = await FirebaseAdapter().teams.getTeam(teamId);
      return team
        ? {
            id: team.id,
            name: team.name,
            isPrimary: team.id === primaryTeamId,
          }
        : null;
    }),
  );

  return teams.filter(Boolean);
}

/**
 * Clear primary team if it matches the given team (used when team is deleted)
 * @param {string} userId The user id
 * @param {string} orgId The organization id
 * @param {string} teamId The team id being deleted
 * @returns {Promise<void>}
 */
export async function clearPrimaryTeamIfMatches(userId, orgId, teamId) {
  const membership = await FirebaseAdapter().membership.getOrgMembership(
    userId,
    orgId,
  );

  if (membership?.primaryTeamId === teamId) {
    // Get other teams the user is in
    const otherTeams = await FirebaseAdapter().membership.listUserTeamsInOrg(
      userId,
      orgId,
    );
    const remainingTeams = otherTeams.filter((id) => id !== teamId);

    // Set to first remaining team, or null if none
    const newPrimaryTeamId =
      remainingTeams.length > 0 ? remainingTeams[0] : null;
    await FirebaseAdapter().membership.setPrimaryTeam(
      userId,
      orgId,
      newPrimaryTeamId,
    );
  }
}
